import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';

// ========================================
// 型定義
// ========================================
interface BillingInvoice {
  id: string;
  invoiceNumber: string;
  constructionPartnerId: string | null;
  partnerName: string | null;
  invoiceDate: string;
  dueDate: string | null;
  subtotal: string | null;
  taxAmount: string | null;
  totalAmount: string | null;
  status: string;
  paymentDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Partner {
  id: string;
  name: string;
  code: string;
  email: string | null;
  contactEmail: string | null;
  phone: string | null;
  address: string | null;
  postalCode: string | null;
}

interface ConstructionForBilling {
  id: string;
  constructionNumber: string;
  title: string;
  constructionDate: string | null;
  demandLocationName: string | null;
  companyId: string;
  companyName: string;
  constructionPartnerId: string | null;
  products: ConstructionProduct[] | null;
  status: string;
  orderAmount: string | null;
}

interface ConstructionProduct {
  id?: string;
  productName: string;
  productModel?: string;
  quantity: number;
  unitPrice: number;
}

const formatCurrency = (amount: number | string | null) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
  return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(num);
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
};

const statusLabels: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'warning' | 'danger' }> = {
  draft: { label: '下書き', variant: 'default' },
  sent: { label: '送信済', variant: 'info' },
  paid: { label: '入金済', variant: 'success' },
  cancelled: { label: 'キャンセル', variant: 'danger' },
};

// ========================================
// メインコンポーネント
// ========================================
export default function BillingInvoiceList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL同期State
  const currentPage = parseInt(searchParams.get('page') || '1');
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const sortBy = searchParams.get('sortBy') || 'created_at';
  const sortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc';
  const filterStatus = searchParams.get('status') || '';
  const filterPartnerId = searchParams.get('partnerId') || '';
  const filterStartDate = searchParams.get('startDate') || '';
  const filterEndDate = searchParams.get('endDate') || '';
  const filterKeyword = searchParams.get('keyword') || '';

  const setCurrentPage = (page: number) => {
    setSearchParams(prev => { prev.set('page', String(page)); return prev; }, { replace: true });
  };
  const setFilterStatus = (v: string) => {
    setSearchParams(prev => { if (v) prev.set('status', v); else prev.delete('status'); prev.set('page', '1'); return prev; }, { replace: true });
  };
  const setFilterPartnerId = (v: string) => {
    setSearchParams(prev => { if (v) prev.set('partnerId', v); else prev.delete('partnerId'); return prev; }, { replace: true });
  };
  const setFilterStartDate = (v: string) => {
    setSearchParams(prev => { if (v) prev.set('startDate', v); else prev.delete('startDate'); return prev; }, { replace: true });
  };
  const setFilterEndDate = (v: string) => {
    setSearchParams(prev => { if (v) prev.set('endDate', v); else prev.delete('endDate'); return prev; }, { replace: true });
  };
  const setFilterKeyword = (v: string) => {
    setSearchParams(prev => { if (v) prev.set('keyword', v); else prev.delete('keyword'); return prev; }, { replace: true });
  };

  // 一覧State
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // パートナー一覧
  const [partners, setPartners] = useState<Partner[]>([]);

  // 工事選択モーダル
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [constructions, setConstructions] = useState<ConstructionForBilling[]>([]);
  const [selectedConstructions, setSelectedConstructions] = useState<Set<string>>(new Set());
  const [selectedProducts, setSelectedProducts] = useState<Map<string, Set<number>>>(new Map());
  const [billedItems, setBilledItems] = useState<{ constructionId: string; productIndex: number; invoiceNumber: string }[]>([]);
  const [modalPartnerId, setModalPartnerId] = useState('');
  const [modalKeyword, setModalKeyword] = useState('');
  const [loadingConstructions, setLoadingConstructions] = useState(false);
  const [creating, setCreating] = useState(false);

  // 削除確認モーダル
  const [deleteTarget, setDeleteTarget] = useState<BillingInvoice | null>(null);

  // 検索トリガー（URLパラメータ変更時にuseEffectが発火するため不要になったが互換用に残す）
  const [searchTrigger, setSearchTrigger] = useState(0);

  // ========================================
  // データ取得
  // ========================================
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);
      if (filterStatus) params.append('status', filterStatus);
      if (filterPartnerId) params.append('partnerId', filterPartnerId);
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);
      if (filterKeyword) params.append('keyword', filterKeyword);

      const data = await api.get<{ items: BillingInvoice[]; total: number }>(`/api/billing-invoices?${params}`);
      setInvoices(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch billing invoices:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const data = await api.get<{ items: Partner[] }>('/api/billing-invoices/partners/list');
      setPartners(data.items || []);
    } catch {
      setPartners([]);
    }
  };

  const fetchConstructionsForBilling = async () => {
    setLoadingConstructions(true);
    try {
      const params = new URLSearchParams();
      if (modalPartnerId) params.append('partnerId', modalPartnerId);
      if (modalKeyword) params.append('keyword', modalKeyword);
      const data = await api.get<{ items: ConstructionForBilling[] }>(`/api/billing-invoices/constructions/for-billing?${params}`);
      setConstructions(data.items || []);

      // 重複チェック
      const constructionIds = (data.items || []).map(c => c.id);
      if (constructionIds.length > 0) {
        const dupCheck = await api.post<{ billedItems: { constructionId: string; productIndex: number; invoiceNumber: string }[] }>(
          '/api/billing-invoices/check-duplicates',
          { constructionIds }
        );
        setBilledItems(dupCheck.billedItems || []);
      }
    } catch {
      setConstructions([]);
    } finally {
      setLoadingConstructions(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, pageSize, sortBy, sortOrder, filterStatus, filterPartnerId, filterStartDate, filterEndDate, filterKeyword, searchTrigger]);

  const handleSearch = () => {
    setSearchParams(prev => { prev.set('page', '1'); return prev; }, { replace: true });
    setSearchTrigger(prev => prev + 1);
  };

  const handleSort = (key: string) => {
    let newKey = key;
    let newOrder: string;
    if (sortBy !== key) {
      newOrder = 'asc';
    } else if (sortOrder === 'asc') {
      newOrder = 'desc';
    } else {
      newKey = 'created_at';
      newOrder = 'desc';
    }
    setSearchParams(prev => {
      prev.set('sortBy', newKey);
      prev.set('sortOrder', newOrder);
      prev.set('page', '1');
      return prev;
    }, { replace: true });
  };

  // ========================================
  // 工事選択モーダル
  // ========================================
  const openCreateModal = () => {
    setShowCreateModal(true);
    setSelectedConstructions(new Set());
    setSelectedProducts(new Map());
    setModalPartnerId('');
    setModalKeyword('');
    fetchConstructionsForBilling();
  };

  const handleModalSearch = () => {
    fetchConstructionsForBilling();
  };

  const isProductBilled = (constructionId: string, productIndex: number) => {
    return billedItems.some(
      b => b.constructionId === constructionId && b.productIndex === productIndex
    );
  };

  const toggleConstruction = (constructionId: string, products: ConstructionProduct[] | null) => {
    const newSelected = new Set(selectedConstructions);
    const newSelectedProducts = new Map(selectedProducts);

    if (newSelected.has(constructionId)) {
      newSelected.delete(constructionId);
      newSelectedProducts.delete(constructionId);
    } else {
      newSelected.add(constructionId);
      // 請求済みでない製品を全選択
      const availableIndices = new Set<number>();
      (products || []).forEach((_, idx) => {
        if (!isProductBilled(constructionId, idx)) {
          availableIndices.add(idx);
        }
      });
      newSelectedProducts.set(constructionId, availableIndices);
    }

    setSelectedConstructions(newSelected);
    setSelectedProducts(newSelectedProducts);
  };

  const toggleProduct = (constructionId: string, productIndex: number) => {
    const newSelectedProducts = new Map(selectedProducts);
    const current = newSelectedProducts.get(constructionId) || new Set<number>();
    const newSet = new Set(current);

    if (newSet.has(productIndex)) {
      newSet.delete(productIndex);
    } else {
      newSet.add(productIndex);
    }

    newSelectedProducts.set(constructionId, newSet);

    // 製品が1つも選択されていない場合は工事も選択解除
    if (newSet.size === 0) {
      const newSelected = new Set(selectedConstructions);
      newSelected.delete(constructionId);
      setSelectedConstructions(newSelected);
      newSelectedProducts.delete(constructionId);
    } else if (!selectedConstructions.has(constructionId)) {
      const newSelected = new Set(selectedConstructions);
      newSelected.add(constructionId);
      setSelectedConstructions(newSelected);
    }

    setSelectedProducts(newSelectedProducts);
  };

  // ========================================
  // 請求書作成
  // ========================================
  const handleCreateInvoice = async () => {
    if (selectedConstructions.size === 0 || creating) return;

    setCreating(true);
    try {
      // 選択された製品からアイテムを構築
      const items: {
        constructionId: string;
        productIndex: number;
        constructionDate?: string;
        companyName?: string;
        productName: string;
        model?: string;
        quantity: number;
        unitPrice: number;
        amount: number;
      }[] = [];

      selectedConstructions.forEach(cId => {
        const construction = constructions.find(c => c.id === cId);
        if (!construction) return;

        const productIndices = selectedProducts.get(cId) || new Set<number>();
        const products = construction.products || [];

        productIndices.forEach(idx => {
          const product = products[idx];
          if (!product) return;

          items.push({
            constructionId: cId,
            productIndex: idx,
            constructionDate: construction.constructionDate || undefined,
            companyName: construction.demandLocationName || construction.companyName || '',
            productName: product.productName,
            model: product.productModel,
            quantity: product.quantity || 1,
            unitPrice: product.unitPrice || 0,
            amount: (product.quantity || 1) * (product.unitPrice || 0),
          });
        });
      });

      if (items.length === 0) {
        toast.error('製品が選択されていません');
        setCreating(false);
        return;
      }

      // 支払期限: 翌月末
      const today = new Date();
      const dueDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      const dueDateStr = dueDate.toISOString().split('T')[0];

      const result = await api.post<BillingInvoice>('/api/billing-invoices', {
        constructionPartnerId: modalPartnerId || undefined,
        invoiceDate: today.toISOString().split('T')[0],
        dueDate: dueDateStr,
        items,
      });

      setShowCreateModal(false);
      // 詳細画面に遷移
      navigate(`/billing-invoices/${result.id}`);
    } catch (error) {
      console.error('Failed to create billing invoice:', error);
      toast.error('請求書の作成に失敗しました');
    } finally {
      setCreating(false);
    }
  };

  // ========================================
  // 削除
  // ========================================
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/billing-invoices/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchInvoices();
    } catch (error) {
      console.error('Failed to delete billing invoice:', error);
      toast.error('削除に失敗しました');
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  // 選択された製品数を計算
  const selectedProductCount = Array.from(selectedProducts.values()).reduce((sum, s) => sum + s.size, 0);

  return (
    <div className="space-y-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">請求書管理</h1>
        <Button onClick={openCreateModal}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          請求書作成
        </Button>
      </div>

      {/* フィルタ */}
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
          <Input
            placeholder="キーワード検索..."
            value={filterKeyword}
            onChange={(e) => setFilterKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Select value={filterPartnerId} onChange={(e) => setFilterPartnerId(e.target.value)}>
            <option value="">全施工パートナー</option>
            {partners.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
          <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">全ステータス</option>
            <option value="draft">下書き</option>
            <option value="sent">送信済</option>
            <option value="paid">入金済</option>
            <option value="cancelled">キャンセル</option>
          </Select>
          <Input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            placeholder="開始日"
          />
          <div className="flex gap-2">
            <Input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              placeholder="終了日"
            />
            <Button onClick={handleSearch} className="shrink-0">
              反映
            </Button>
          </div>
        </div>
      </Card>

      {/* 一覧 */}
      <Card>
        {loading ? (
          <div className="text-center py-12 text-gray-500">読み込み中...</div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>請求書がありません</p>
            <Button variant="secondary" className="mt-4" onClick={openCreateModal}>
              請求書を作成する
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('invoice_number')}
                    >
                      請求書番号 {sortBy === 'invoice_number' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      施工パートナー
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('invoice_date')}
                    >
                      請求日 {sortBy === 'invoice_date' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('total_amount')}
                    >
                      合計金額 {sortBy === 'total_amount' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('status')}
                    >
                      ステータス {sortBy === 'status' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      支払期限
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {invoices.map((invoice) => {
                    const statusInfo = statusLabels[invoice.status] || { label: invoice.status, variant: 'default' as const };
                    return (
                      <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-4 py-3">
                          <Link
                            to={`/billing-invoices/${invoice.id}`}
                            className="text-blue-600 hover:underline font-medium"
                          >
                            {invoice.invoiceNumber}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {invoice.partnerName || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(invoice.invoiceDate)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-gray-100">
                          {formatCurrency(invoice.totalAmount)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(invoice.dueDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Link to={`/billing-invoices/${invoice.id}`}>
                              <Button variant="secondary" size="sm">詳細</Button>
                            </Link>
                            {invoice.status === 'draft' && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => setDeleteTarget(invoice)}
                              >
                                削除
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* 工事選択モーダル */}
      <Modal
          isOpen={showCreateModal}
          title="請求書を作成 - 工事データを選択"
          onClose={() => setShowCreateModal(false)}
          size="xl"
        >
          <div className="space-y-4">
            {/* フィルタ */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select value={modalPartnerId} onChange={(e) => setModalPartnerId(e.target.value)}>
                <option value="">全施工パートナー</option>
                {partners.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
              <Input
                placeholder="キーワード検索..."
                value={modalKeyword}
                onChange={(e) => setModalKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleModalSearch()}
              />
              <Button onClick={handleModalSearch}>検索</Button>
            </div>

            {/* 選択サマリー */}
            {selectedProductCount > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <span className="font-medium">{selectedConstructions.size}</span> 件の工事から
                  <span className="font-medium"> {selectedProductCount}</span> 件の製品を選択中
                </p>
              </div>
            )}

            {/* 工事一覧 */}
            <div className="max-h-[500px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              {loadingConstructions ? (
                <div className="text-center py-8 text-gray-500">読み込み中...</div>
              ) : constructions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">工事データが見つかりません</div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {constructions.map((construction) => {
                    const products = construction.products || [];
                    const isSelected = selectedConstructions.has(construction.id);
                    const allBilled = products.length > 0 && products.every((_, idx) => isProductBilled(construction.id, idx));

                    return (
                      <div key={construction.id} className={`p-3 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}>
                        {/* 工事ヘッダー */}
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={allBilled || products.length === 0}
                            onChange={() => toggleConstruction(construction.id, products)}
                            className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {construction.constructionNumber}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {construction.title}
                              </span>
                              {allBilled && (
                                <Badge variant="warning">全品請求済</Badge>
                              )}
                            </div>
                            <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400 mt-1">
                              <span>施工日: {formatDate(construction.constructionDate)}</span>
                              <span>企業: {construction.companyName || '-'}</span>
                              <span>需要場所: {construction.demandLocationName || '-'}</span>
                            </div>

                            {/* 製品一覧 */}
                            {products.length > 0 && isSelected && (
                              <div className="mt-2 ml-2 space-y-1">
                                {products.map((product, idx) => {
                                  const billed = isProductBilled(construction.id, idx);
                                  const billedInfo = billedItems.find(
                                    b => b.constructionId === construction.id && b.productIndex === idx
                                  );
                                  const isProductSelected = selectedProducts.get(construction.id)?.has(idx) || false;

                                  return (
                                    <div
                                      key={idx}
                                      className={`flex items-center gap-2 text-sm p-1 rounded ${billed ? 'opacity-50' : ''}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isProductSelected}
                                        disabled={billed}
                                        onChange={() => toggleProduct(construction.id, idx)}
                                        className="h-3 w-3 text-blue-600 rounded border-gray-300"
                                      />
                                      <span className={`flex-1 ${billed ? 'line-through' : ''}`}>
                                        {product.productName}
                                        {product.productModel ? ` (${product.productModel})` : ''}
                                      </span>
                                      <span className="text-gray-500">
                                        {product.quantity || 1} × {formatCurrency(product.unitPrice || 0)}
                                      </span>
                                      <span className="font-medium">
                                        {formatCurrency((product.quantity || 1) * (product.unitPrice || 0))}
                                      </span>
                                      {billed && (
                                        <span className="text-xs text-orange-600 dark:text-orange-400">
                                          ({billedInfo?.invoiceNumber}で請求済)
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* アクション */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                キャンセル
              </Button>
              <Button
                onClick={handleCreateInvoice}
                disabled={selectedProductCount === 0}
                loading={creating}
              >
                請求書を作成 ({selectedProductCount}件)
              </Button>
            </div>
          </div>
        </Modal>

      {/* 削除確認モーダル */}
      <Modal
          isOpen={!!deleteTarget}
          title="請求書の削除"
          onClose={() => setDeleteTarget(null)}
        >
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              請求書 <span className="font-bold">{deleteTarget?.invoiceNumber}</span> を削除しますか？
            </p>
            <p className="text-sm text-red-600">この操作は取り消せません。</p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
                キャンセル
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                削除する
              </Button>
            </div>
          </div>
        </Modal>
    </div>
  );
}
